/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useEffect, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import { Grid } from '@mui/material';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useGetLayoutLanguages } from 'api';
import {
  IDynamicUiField,
  IComponentParams,
  IDynamicAction,
  ILoadDataParam,
  IDynamicComponent
} from 'plugins/api/types';
import { Modal } from 'components/controls';
import { useLoadData } from 'plugins/api/endpoints';
import { loadDataUrlParser } from 'plugins/util/parser';
import { SubComponent } from 'plugins/components/containers';
import {
  DynamicSelect,
  DynamicList,
  DynamicDecimal,
  DynamicText,
  DynamicTextArea,
  DynamicCheckbox,
  DynamicTranslations,
  DynamicDate,
  DynamicEmpty,
  DynamicTile,
  DynamicFile,
  Action
} from 'plugins/components/controls';
import { DynamicSubList } from 'plugins/components/controls/DynamicList/components';

import 'plugins/theme/components/EditPanel/EditPanel.scss';

// typ danych wejściowych
interface IProps {
  label: string;
  isModal?: boolean;
  fields: IDynamicUiField[];
  actions: IDynamicAction[];
  componentParams?: IComponentParams;
  requestParams?: object;
  loadDataParams?: ILoadDataParam;
  pageSymbol: string;
  componentSymbol: string;
  componentsList?: IDynamicComponent[];
  isSubComponent?: boolean;
}

const EditPanel: FC<IProps> = ({
  label,
  isModal = true,
  fields,
  componentParams,
  requestParams,
  actions,
  loadDataParams,
  pageSymbol,
  componentSymbol,
  componentsList,
  isSubComponent
}) => {
  const { search } = useLocation();
  const dispatch = useDispatch();

  // czy jest widoczny modal
  const [isVisible, setIsVisible] = useState(componentParams?.isEditorInitialVisible);

  // pobieranie globalnego stanu kontekstu danych
  const { contextData } = useSelector((state) => state.dynamicPage);

  const loadDataUrl = loadDataUrlParser(loadDataParams?.get_url || '', {
    ...requestParams,
    ...qs.parse(search)
  });

  // pobranie danych tabeli
  const { data: editPanelData, refetch: refetchEditPanelData } = useLoadData(
    loadDataUrl,
    {},
    { enabled: false }
  );

  // pobranie listy dostępnych języków
  const { data: languagesData } = useGetLayoutLanguages();

  // pobranie danych początkowych
  useEffect(() => {
    if (loadDataParams) {
      refetchEditPanelData();
    }
  }, [loadDataParams]);

  // ustalenie początkowego stanu requestState
  useEffect(() => {
    const arr = fields?.map((field) => {
      // if (field.type === 'translation' && field.value === null) {
      //   return {
      //     [field.field_symbol]: []
      //   };
      // }

      const fieldSymbol = (editPanelData as any)?.[field.field_symbol];

      return {
        [field.field_symbol]:
          fieldSymbol !== null && fieldSymbol !== undefined ? fieldSymbol : field.value
      };
    });

    // sub_dictionary: dodatkowe wpisy dla pól sub-słownika
    const subDictArr = (fields || [])
      .filter((field) => field.dictionary?.sub_dictionary?.field_symbol)
      .map((field) => {
        const subFieldSymbol = field.dictionary.sub_dictionary!.field_symbol;
        const subValue = (editPanelData as any)?.[subFieldSymbol];
        return {
          [subFieldSymbol]: subValue !== null && subValue !== undefined ? subValue : null
        };
      });

    dispatch(
      reduxActions.setContextData({
        [pageSymbol]: {
          [componentSymbol]: mapValues(
            keyBy([...(arr || []), ...subDictArr], (key: string) => Object.keys(key)[0]),
            (value) => Object.values(value)[0]
          )
        }
      })
    );
  }, [editPanelData, fields]);

  useEffect(() => {
    setIsVisible(componentParams?.isEditorInitialVisible);
  }, [componentParams?.isEditorInitialVisible]);

  const handleClose = () => {
    setIsVisible(false), componentParams?.fnCallback?.(false);
  };

  const renderContent = (field: IDynamicUiField, i: number) => {
    const { value, field_symbol, ...restFields } = field;

    const mapedValue =
      (editPanelData as any)?.[field_symbol] !== null &&
      (editPanelData as any)?.[field_symbol] !== undefined
        ? (editPanelData as any)?.[field_symbol]
        : value;

    const mapField = {
      field_symbol,
      ...restFields,
      value: mapedValue
    };

    if (field.type === 'select') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicSelect
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
          />
        </Grid>
      );
    }

    if (field.type === 'list') {
      const subDict = mapField.dictionary?.sub_dictionary;
      const parentValue = subDict
        ? (contextData?.[pageSymbol]?.[componentSymbol]?.[field.field_symbol] as string) || null
        : null;

      return (
        <Fragment key={i}>
          <Grid item xs={field.width} className="gridItem" justifyContent="flex-start">
            <DynamicList
              field={mapField}
              pageSymbol={pageSymbol}
              componentSymbol={componentSymbol}
              currentValue={(contextData?.[pageSymbol]?.[componentSymbol]?.[field.field_symbol] as string) || null}
            />
          </Grid>
          {subDict && (
            <Grid item xs={field.width} className="gridItem" justifyContent="flex-start">
              <DynamicSubList
                parentField={mapField}
                subDictionary={subDict}
                parentValue={parentValue}
                currentValue={(contextData?.[pageSymbol]?.[componentSymbol]?.[subDict.field_symbol] as string) || null}
                pageSymbol={pageSymbol}
                componentSymbol={componentSymbol}
              />
            </Grid>
          )}
        </Fragment>
      );
    }

    if (field.type === 'decimal' || field.type === 'integer' || field.type === 'float') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicDecimal
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
          />
        </Grid>
      );
    }

    if (field.type === 'text' || field.type === 'string') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicText field={mapField} pageSymbol={pageSymbol} componentSymbol={componentSymbol} />
        </Grid>
      );
    }

    if (field.type === 'textarea') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicTextArea
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
          />
        </Grid>
      );
    }

    if (field.type === 'translation') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicTranslations
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
            languagesData={languagesData?.items}
          />
        </Grid>
      );
    }

    if (field.type === 'boolean') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicCheckbox
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
          />
        </Grid>
      );
    }

    if (field.type === 'date' || field.type === 'datetime') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicDate
            field={mapField}
            pageSymbol={pageSymbol}
            componentSymbol={componentSymbol}
            isTimePicker={field.type === 'datetime'}
          />
        </Grid>
      );
    }

    if (field.type === 'file') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicFile field={mapField} pageSymbol={pageSymbol} componentSymbol={componentSymbol} />
        </Grid>
      );
    }

    if (field.type === 'tile') {
      return (
        <Grid item xs={field.width} key={i} className="gridItem" justifyContent="flex-start">
          <DynamicTile field={field} pageSymbol={pageSymbol} componentSymbol={componentSymbol} />
        </Grid>
      );
    }

    if (field.type === 'empty') {
      return <DynamicEmpty fields={fields} field={field} index={i} />;
    }

    return null;
  };

  const renderSubComponent = () => {
    const subComponentsActionList = actions?.filter((action) => !!action.sub_components_to_refresh);

    return (subComponentsActionList || []).map((subComponentAction, i) => {
      const activeSubcomponent = componentsList?.find(
        (component) => component.symbol === subComponentAction.sub_components_to_refresh
      );
      if (activeSubcomponent) {
        return (
          <Fragment key={i}>
            <SubComponent
              componentSymbol={subComponentAction.sub_components_to_refresh || ''}
              pageSymbol={pageSymbol}
              parentComponentSymbol={componentSymbol}
              paramsList={activeSubcomponent.params_list}
              requestParams={requestParams}
            />
          </Fragment>
        );
      }

      return null;
    });
  };

  const renderActions = (action: IDynamicAction, i: number) => {
    if (action.type) {
      return (
        <Action
          key={i}
          action={action}
          handleClose={() => handleClose()}
          refetchEditPanelData={refetchEditPanelData}
          requestParams={requestParams}
          requestState={contextData}
          componentParams={componentParams}
          pageSymbol={pageSymbol}
        />
      );
    }

    return null;
  };

  const renderEditPanel = () => {
    return (
      <div className="EditPanel-ContentWrapper">
        {label && <div>{isSubComponent ? <h3>{label}</h3> : <h2>{label}</h2>}</div>}

        <div className="EditPanel-FormCard">
          {fields && (
            <div className="EditPanel-Content">
              <Grid container spacing={3}>
                {fields?.map((field, i) => renderContent(field, i))}
              </Grid>
            </div>
          )}

          <div className="EditPanel-ActionWrapper">
            {actions && actions.map((action, i) => renderActions(action, i))}
          </div>
        </div>

        {renderSubComponent()}
      </div>
    );
  };

  if (isVisible && isModal) {
    return (
      <Modal title={label} onClose={() => handleClose()}>
        {renderEditPanel()}
      </Modal>
    );
  }

  return renderEditPanel();
};

export default EditPanel;
